import {Spin} from 'antd';
import {FormInstance} from 'antd/es/form/Form';
import React, {Key, useEffect, useState, useCallback} from 'react';

import {useCustomTable} from 'components/@common/widgets/custom-table/custom-table';
import {selectContextRawData} from 'modules/context/context-selector';
import {usePagination} from 'shared/hooks/use-pagination';
import {showMessageFromResponse} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';

import {IOrgInfoDto, IPostDataOrgInfoArgs} from '../../../api/types';
import {useOrgInfoColumns} from '../../../hooks/use-org-info-columns';
import {OrgInfoTabService} from '../../../services';
import {ActionPanel} from '../../action-panel';
import {OrgInfoForm} from '../../forms/org-info-form';

export const OrgInfoTab = () => {
    const [tableData, setTableData] = useState<IOrgInfoDto[]>();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const [isLoadingTableData, setIsLoadingTableData] = useState<boolean>(false);

    const [totalRows, setTotalRows] = useState<number>(0);

    const [currentPage, setCurrentPage] = useState<number>(1);

    const {pageSize, handleChange} = usePagination({pageDefault: 1, pageSizeDefault: 10});
    const {organizationId} = useAppSelector(selectContextRawData) ?? {};

    const fetchData = useCallback(async () => {
        try {
            setIsLoadingTableData(true);
            const {data} = await OrgInfoTabService.get({paginationPageSize: pageSize, paginationCurrent: currentPage});
            setTableData(data.dataTable);
            setTotalRows(data.recordsTotal);
            setIsLoadingTableData(false);
        } catch (e) {
            console.error('Ошибка получения записей: ', e);
        }
    }, [pageSize, currentPage]);

    const columns = useOrgInfoColumns(tableData, fetchData);

    useEffect(() => {
        fetchData();
    }, [pageSize, currentPage, organizationId]);

    const {customTableJSX} = useCustomTable({
        rowKey: 'id',
        pagination: {
            position: ['bottomLeft'],
            total: (() => totalRows)(),
            pageSize,
            onChange: (value: number) => {
                handleChange(value);
                setCurrentPage(value);
            },
        },
        rowSelection: {selectedRowKeys, onChange: (selectedTableRowKeys) => setSelectedRowKeys(selectedTableRowKeys)},
        className: 'mt-5',
        dataSource: tableData,
        bordered: true,
        columns,
    });

    const handleDeleteRows = useCallback(async (rowsIds: Key[]) => {
        try {
            await OrgInfoTabService.delete({rowsIds});
            setSelectedRowKeys([]);
            await fetchData();
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleFinish = useCallback(async (values: IPostDataOrgInfoArgs) => {
        try {
            const {response, isError} = await OrgInfoTabService.post(values);

            if (isError) {
                showMessageFromResponse({response, isError});
            }

            await fetchData();
        } catch (e) {
            console.error(e);
        }
    }, []);

    return (
        <>
            <ActionPanel<IPostDataOrgInfoArgs>
                config={{selectedRowKeys, handleDeleteRows, handleFinish, title: 'Сведения об организации'}}
            >
                {(formInstance: FormInstance, onFinish?: (values: IPostDataOrgInfoArgs) => Promise<void>) => (
                    <OrgInfoForm<IPostDataOrgInfoArgs>
                        formInstance={formInstance}
                        onFinish={onFinish}
                    />
                )}
            </ActionPanel>
            <Spin spinning={isLoadingTableData}>{customTableJSX}</Spin>
        </>
    );
};
